import CryptoJS from 'crypto-js';

// Secret key - in production, this should be in environment variables
const SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'ConnectHealthPro2024SecretKey';

/**
 * Encrypts a userId to create a referral code
 * @param {number|string} userId - The user ID to encrypt
 * @returns {string} Encrypted referral code (Base64 URL-safe)
 */
export const encryptUserId = (userId) => {
    try {
        if (!userId) return '';
        
        // Convert to string and encrypt
        const userIdStr = userId.toString();
        const encrypted = CryptoJS.AES.encrypt(userIdStr, SECRET_KEY).toString();
        
        // Make URL-safe by replacing problematic characters
        const urlSafe = encrypted
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '~');
        
        return urlSafe;
    } catch (error) {
        console.error('Encryption error:', error);
        return '';
    }
};

/**
 * Decrypts a referral code to get the userId
 * @param {string} encryptedCode - The encrypted referral code
 * @returns {number|null} Decrypted userId or null if invalid
 */
export const decryptUserId = (encryptedCode) => {
    try {
        if (!encryptedCode || typeof encryptedCode !== 'string') return null;
        
        // Restore from URL-safe format
        const restored = encryptedCode
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            .replace(/~/g, '=');
        
        // Decrypt
        const decrypted = CryptoJS.AES.decrypt(restored, SECRET_KEY);
        const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);
        
        if (!decryptedStr) return null;
        
        // Parse to integer
        const userId = parseInt(decryptedStr, 10);
        
        return isNaN(userId) || userId <= 0 ? null : userId;
    } catch (error) {
        console.error('Decryption error:', error);
        return null;
    }
};

/**
 * Validates if a string is a valid encrypted referral code
 * @param {string} code - The code to validate
 * @returns {boolean} True if code can be decrypted
 */
export const isValidReferralCode = (code) => {
    const decrypted = decryptUserId(code);
    return decrypted !== null && decrypted > 0;
};
